import {
  eventFeed,
  EventFeedFilterGroupKeys,
  EventFeedFiltersSchemas,
} from '@toggle/toggle';

import { SharedFilterState } from '~/hooks/use-filter-actions/useFilterActions';
import {
  STREAM_SELECTED_WATCHLISTS,
  useRemoteStorage,
} from '~/hooks/use-remote-storage';
import { apiFetch } from '~/utils/api-fetch/apiFetch';
import { mapEventFeedFiltersSchemasToFilterGroups } from '~/utils/event-feed-filters/mapEventFeedSchemasToScreenerFilters';
import { create } from '~/utils/store/createStore';
import { StreamFilterTabs } from '~/views/stream/components/stream-type-tab-line/StreamTypeTabLine';

import {
  defaultEventFeedFilterGroups,
  tabToEventFeedFilterGroup,
  tabTypeToEventFeedType,
} from './stream-filters-shared';

export { tabToEventFeedFilterGroup, tabTypeToEventFeedType };

interface StreamFiltersStore
  extends SharedFilterState<EventFeedFilterGroupKeys> {
  selectedWatchlistIds: Set<string>;
  isSecondRelationshipEnabled: boolean;
  selectedTabType: StreamFilterTabs;
  isInitialized: boolean;
  initializeFromStorage: () => void;
  initEventFeedFilters: () => Promise<void>;
  setSelectedWatchlistIds: (ids: Set<string>) => void;
  setIsSecondRelationshipEnabled: (enabled: boolean) => void;
  handleToggleSecondRelationship: () => void;
  setSelectedTabType: (type: StreamFilterTabs) => void;
}

export const useStreamFilters = create<StreamFiltersStore>(set => ({
  selectedWatchlistIds: new Set<string>(),
  isSecondRelationshipEnabled: false,
  selectedTabType: 'all',
  isInitialized: false,
  filterGroups: defaultEventFeedFilterGroups,
  initializeFromStorage: () => {
    const { items } = useRemoteStorage.getState();
    const storedIds = Array.isArray(items?.[STREAM_SELECTED_WATCHLISTS])
      ? items?.[STREAM_SELECTED_WATCHLISTS]
      : undefined;

    if (storedIds?.length) {
      set({ selectedWatchlistIds: new Set(storedIds), isInitialized: true });
    } else {
      set({ isInitialized: true });
    }
  },
  initEventFeedFilters: async () => {
    const res = await apiFetch<EventFeedFiltersSchemas>(
      eventFeed.getFilters.path,
      { method: 'get' }
    );

    if (!res.error) {
      set({
        filterGroups: mapEventFeedFiltersSchemasToFilterGroups(
          res.data,
          defaultEventFeedFilterGroups
        ),
      });
    }
  },
  setSelectedWatchlistIds: ids => {
    set({ selectedWatchlistIds: ids });

    const { storeItems } = useRemoteStorage.getState();
    storeItems({ [STREAM_SELECTED_WATCHLISTS]: Array.from(ids) });
  },
  setIsSecondRelationshipEnabled: enabled => {
    set({ isSecondRelationshipEnabled: enabled });
  },
  handleToggleSecondRelationship: () => {
    set(state => ({
      isSecondRelationshipEnabled: !state.isSecondRelationshipEnabled,
    }));
  },
  setSelectedTabType: type => {
    set({ selectedTabType: type });
  },
}));
